import { Box, IconButton, List, styled, Toolbar, Typography, Drawer as MuiDrawer, ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse, Container, ListItemAvatar, Avatar, Menu, MenuItem as MuiMenuItem, ToggleButtonGroup, ToggleButton } from '@mui/material';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import './Drawer.css'
import { memo, useCallback, useMemo, useState, ReactElement, ReactNode, RefObject } from 'react';
import { ReactComponent as LinkertoIcon } from '../../assets/icons/linkerto_icon.svg';
import { HiMenu, HiOutlineLogout } from "react-icons/hi";
import { IoIosArrowUp, IoIosArrowDown, IoMdSettings } from "react-icons/io";
import { BsThreeDotsVertical } from "react-icons/bs";
import { BiSolidDashboard } from "react-icons/bi";
import { FaClipboardUser } from "react-icons/fa6";
import { PiLinkSimpleHorizontalBold } from "react-icons/pi";
import { AiFillEye } from "react-icons/ai";
import { MdInvertColors } from "react-icons/md";
import AppBar from '@mui/material/AppBar';
import { FaAngleLeft } from "react-icons/fa6";
import { FiSidebar } from "react-icons/fi";
import { Link, useNavigate } from 'react-router-dom';
import { closedMixin, getAvatarUrl, getInitials, openedMixin } from '../../utils';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useTranslation } from 'react-i18next';
import { APP_VERSION, DRAWER_WIDTH } from 'utils/constants';
import { useDashboard } from 'context/DashboardContext';
import { DashboardView } from 'utils/enums';

interface MenuSubItem {
  id: DashboardView;
  label: ReactNode;
  icon: ReactElement;
}

interface MenuItem {
  id: DashboardView;
  labelKey: string;
  icon: ReactElement;
  subitems?: MenuSubItem[];
}

const MENU_ITEMS: MenuItem[] = [
  {
    id: DashboardView.DASHBOARD,
    labelKey: "dashboard.nav.dashboard",
    icon: <BiSolidDashboard />
  },
  {
    id: DashboardView.PROFILE,
    labelKey: "dashboard.nav.profile",
    icon: <FaClipboardUser />
  },
  {
    id: DashboardView.LINKS,
    labelKey: "dashboard.nav.links",
    icon: <PiLinkSimpleHorizontalBold />
  },
  {
    id: DashboardView.APPEARANCE,
    labelKey: "dashboard.nav.appearance",
    icon: <MdInvertColors />
  },
  {
    id: DashboardView.PREVIEW,
    labelKey: "dashboard.nav.preview",
    icon: <AiFillEye />
  },
  {
    id: DashboardView.SETTINGS,
    labelKey: "dashboard.nav.settings",
    icon: <IoMdSettings />
  },
];

const ToolbarMobile = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: theme.spacing(0, 0.7),
  ...theme.mixins.toolbar,
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: theme.spacing(0, 0.7),
  height: '64px',
  ...theme.mixins.toolbar,
}));

const DrawerFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: theme.spacing(0, 0.7),
  height: '64px',
  ...theme.mixins.toolbar,
}));

const StyledDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: DRAWER_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }: { open: boolean }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }: { open: boolean }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

interface DrawerListProps {
  view: DashboardView;
  open: boolean;
  mobileDrawerOpen: boolean;
  openSubitem: Record<string, boolean>;
  handleClickSubmenu: (id: DashboardView) => void;
  handleClickItem: (id: DashboardView) => void;
}

const DrawerList = memo(({ view, open, mobileDrawerOpen, openSubitem, handleClickSubmenu, handleClickItem }: DrawerListProps) => {
  const { i18n, t } = useTranslation();
  const isOpen = open || mobileDrawerOpen;

  return (
    <List className='drawer-items scrollable' sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {MENU_ITEMS.map((item) => (
        <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => item.subitems && item.subitems.length > 0 ? handleClickSubmenu(item.id) : handleClickItem(item.id)}
            selected={view === item.id}
            sx={[
              {
                minHeight: 48,
                px: 2.5,
                backgroundColor: view.includes(":") ? view.split(":")[0] === item.id ? 'rgba(255, 255, 255, 0.08) !important' : 'inherit' : 'inherit'
              },
              isOpen ? { justifyContent: 'initial' } : { justifyContent: 'center' },
            ]}
          >
            <ListItemIcon
              sx={[
                { minWidth: 0, justifyContent: 'center' },
                isOpen ? { mr: 3 } : { mr: 'auto' },
              ]}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={t(item.labelKey)}
              sx={[isOpen ? { opacity: 1 } : { opacity: 0 }]}
            />
            {item.subitems && item.subitems.length > 0 && (openSubitem[item.id] ? <IoIosArrowDown size={12} /> : <IoIosArrowUp size={12} />)}
          </ListItemButton>
          {isOpen && item.subitems && item.subitems.length > 0 && (
            <>
              {item.subitems.map((subitem) => (
                <Collapse key={subitem.id} in={openSubitem[item.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton onClick={() => handleClickItem(subitem.id)} sx={{ pl: 4 }} selected={view === subitem.id}>
                      <ListItemIcon>{subitem.icon}</ListItemIcon>
                      <ListItemText primary={subitem.label} />
                    </ListItemButton>
                  </List>
                </Collapse>
              ))}
            </>
          )}
        </ListItem>
      ))}
      {isOpen && (
        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, py: 1.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.3 }}>v{APP_VERSION}</Typography>
          <ToggleButtonGroup
            value={i18n.language}
            exclusive
            onChange={(_, lang) => { if (lang) i18n.changeLanguage(lang); }}
            aria-label="select language"
          >
            <ToggleButton value="es" aria-label="language es" sx={{ width: 36, height: 36 }}>ES</ToggleButton>
            <ToggleButton value="en" aria-label="language en" sx={{ width: 36, height: 36 }}>EN</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
    </List>
  );
});

interface DrawerFooterContentProps {
  open: boolean;
  mobileDrawerOpen: boolean;
  avatarSrc: string | null;
  initials: string;
  username?: string;
  plan?: string;
  anchorFooterMenu: HTMLElement | null;
  handleOpenFooterMenu: (event: React.MouseEvent<HTMLElement>) => void;
  handleCloseFooterMenu: () => void;
  handlePushAndClose: () => void;
  handleDrawerOpen: () => void;
  t: (key: string) => string;
}

const DrawerFooterContent = memo(({ open, mobileDrawerOpen, avatarSrc, initials, username, plan, anchorFooterMenu, handleOpenFooterMenu, handleCloseFooterMenu, handlePushAndClose, handleDrawerOpen, t }: DrawerFooterContentProps) => {
  const isOpen = open || mobileDrawerOpen;

  return (
    <DrawerFooter className='drawer-footer'>
      <List className='list-footer'>
        <ListItem className='list-item-footer'>
          <ListItemAvatar onClick={() => !open && handleDrawerOpen()} className='list-item-avatar' sx={{ minWidth: 'auto', display: 'flex', gap: '5px', cursor: !open ? 'pointer' : 'default' }}>
            <Avatar src={avatarSrc ?? undefined} className='drawer-avatar'>
              {!avatarSrc && (
                <span className='drawer-avatar-initials'>{initials}</span>
              )}
            </Avatar>
          </ListItemAvatar>
          <ListItemText className='list-item-text' sx={[isOpen ? { opacity: 1 } : { opacity: 0 }]} primary={username} secondary={plan} />
        </ListItem>
      </List>
      <IconButton
        aria-label="user menu"
        aria-controls={Boolean(anchorFooterMenu) ? 'drawer-footer-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorFooterMenu) ? 'true' : undefined}
        sx={[isOpen ? { opacity: 1 } : { opacity: 0 }]}
        onClick={handleOpenFooterMenu}
      >
        <BsThreeDotsVertical size={16} />
      </IconButton>
      <Menu
        id="drawer-footer-menu"
        anchorEl={anchorFooterMenu}
        keepMounted
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(anchorFooterMenu)}
        onClose={handleCloseFooterMenu}
      >
        <MuiMenuItem onClick={handlePushAndClose}>
          <HiOutlineLogout className="iconProfileMenu" fontSize={20} />
          <Typography variant="body1" color="primary">{t("nav.logout")}</Typography>
        </MuiMenuItem>
      </Menu>
    </DrawerFooter>
  );
});

interface MiniDrawerProps {
  scrollRef?: RefObject<HTMLDivElement | null>;
  children?: ReactNode;
}

const MiniDrawer = ({ scrollRef, children }: MiniDrawerProps) => {
  const { view, setView } = useDashboard();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuStatic, setMenuStatic] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const { user: { userImage, username, plan } } = useAuth();
  const { state } = useProfile();
  const [anchorFooterMenu, setAnchorFooterMenu] = useState<HTMLElement | null>(null);
  const [openSubitem, setOpenSubmenu] = useState<Record<string, boolean>>(() =>
    MENU_ITEMS.reduce((acc, item) => {
      if (item.subitems && item.subitems.length > 0) {
        acc[item.id] = true;
      }
      return acc;
    }, {} as Record<string, boolean>)
  );

  const avatarSrc = useMemo(() => {
    if (!userImage?.fileName) return null;
    return getAvatarUrl(userImage);
  }, [userImage]);

  const title = (state.currentProfile.info as { title?: string })?.title;
  const initials = useMemo(() => getInitials(title, username), [title, username]);

  const handleOpenFooterMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorFooterMenu(event.currentTarget);
  }, []);

  const handleCloseFooterMenu = useCallback(() => {
    setAnchorFooterMenu(null);
  }, []);

  const handlePushAndClose = useCallback(() => {
    setAnchorFooterMenu(null);
    setTimeout(() => {
      navigate("/logout");
    }, 300);
  }, [navigate]);

  const toggleDrawer = useCallback(() => {
    setOpen(prev => !prev);
    setMobileDrawerOpen(prev => !prev);
  }, []);

  const handleDrawerOpen = useCallback(() => {
    toggleDrawer();
    setMenuStatic(prev => !prev);
  }, [toggleDrawer]);

  const handleDrawerClose = useCallback(() => {
    toggleDrawer();
    setMenuStatic(prev => !prev);
  }, [toggleDrawer]);

  const handleMobileDrawerClose = useCallback(() => {
    setTimeout(() => {
      toggleDrawer();
    }, 200);
  }, [toggleDrawer]);

  const handleClickItem = useCallback((id: DashboardView) => {
    setView(id);
    if (!menuStatic && mobileDrawerOpen) {
      setTimeout(() => {
        toggleDrawer();
      }, 200);
    }
  }, [menuStatic, mobileDrawerOpen, setView, toggleDrawer]);

  const handleClickSubmenu = useCallback((id: DashboardView) => {
    if (!open && !mobileDrawerOpen && !menuStatic) {
      toggleDrawer();
      setOpenSubmenu(prev => ({ ...prev, [id]: true }));
    } else {
      setOpenSubmenu(prev => ({ ...prev, [id]: !prev[id] }));
    }
  }, [open, mobileDrawerOpen, menuStatic, toggleDrawer]);

  return (
    <Box className="drawer-container" sx={{ display: 'flex' }}>
      <StyledDrawer className='drawer-mui-drawer' variant="permanent" open={open}>
        <DrawerHeader className='drawer-header desktop'>
          <IconButton onClick={handleDrawerOpen} className='drawer-main-button' disabled={open}>
            <LinkertoIcon className='linkerto-icon' width={30} height={30} />
          </IconButton>
          <Link className="linkerto-button" to="/" >
            <Typography variant="h5" noWrap color="primary" component="div" fontWeight={700}>
              Linkerto
            </Typography>
          </Link>
          <IconButton onClick={handleDrawerClose}>
            <FiSidebar size={16} />
          </IconButton>
        </DrawerHeader>
        <DrawerList
          view={view}
          open={open}
          mobileDrawerOpen={mobileDrawerOpen}
          openSubitem={openSubitem}
          handleClickSubmenu={handleClickSubmenu}
          handleClickItem={handleClickItem}
        />
        <DrawerFooterContent
          open={open}
          mobileDrawerOpen={mobileDrawerOpen}
          avatarSrc={avatarSrc}
          initials={initials}
          username={username}
          plan={plan}
          anchorFooterMenu={anchorFooterMenu}
          handleOpenFooterMenu={handleOpenFooterMenu}
          handleCloseFooterMenu={handleCloseFooterMenu}
          handlePushAndClose={handlePushAndClose}
          handleDrawerOpen={handleDrawerOpen}
          t={t}
        />
      </StyledDrawer>
      <SwipeableDrawer
        className={`drawer-swipeable ${mobileDrawerOpen ? 'open' : ''}`}
        anchor="left"
        open={mobileDrawerOpen}
        onClose={toggleDrawer}
        onOpen={toggleDrawer}
      >
        <Box
          sx={{ width: DRAWER_WIDTH }}
          role="presentation"
          className='drawer-box-mobile'
        >
          <DrawerHeader className='drawer-header mobile'>
            <LinkertoIcon className='linkerto-icon' width={30} height={30} />
            <Typography variant="h5" noWrap component="div" fontWeight={700}>
              Linkerto
            </Typography>
            <IconButton onClick={handleMobileDrawerClose}>
              <FaAngleLeft size={16} />
            </IconButton>
          </DrawerHeader>
          <DrawerList
            view={view}
            open={open}
            mobileDrawerOpen={mobileDrawerOpen}
            openSubitem={openSubitem}
            handleClickSubmenu={handleClickSubmenu}
            handleClickItem={handleClickItem}
          />
          <DrawerFooterContent
            open={open}
            mobileDrawerOpen={mobileDrawerOpen}
            avatarSrc={avatarSrc}
            initials={initials}
            username={username}
            plan={plan}
            anchorFooterMenu={anchorFooterMenu}
            handleOpenFooterMenu={handleOpenFooterMenu}
            handleCloseFooterMenu={handleCloseFooterMenu}
            handlePushAndClose={handlePushAndClose}
            handleDrawerOpen={handleDrawerOpen}
            t={t}
          />
        </Box>
      </SwipeableDrawer>
      <Box className='main-drawer-content' component="main" sx={{ flexGrow: 1, width: '100%', flexDirection: 'column' }}>
        <AppBar className="app-bar-mobile">
          <Container className='app-bar-mobile-container' maxWidth="lg">
            <Toolbar className="tool-bar-mobile">
              <Link className="linkerto-button" to="/" >
                <Typography variant="h5" fontWeight={700}>
                  Linkerto
                </Typography>
              </Link>
              <IconButton onClick={() => setTimeout(() => toggleDrawer(), 200)}>
                <HiMenu />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
        <ToolbarMobile className='mobile-spacer' />
        <Container ref={scrollRef} className='content-children scrollable' maxWidth={false} disableGutters>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default MiniDrawer;
